import React, { useState, useEffect, useRef } from 'react';
import { Search, Check, X } from 'lucide-react';
import type { FilterFieldDef, FilterOption } from '../schema';
import type { ActiveFilter } from '../parser';
import { useI18n } from '@/context/I18nContext';

interface TextFilterEditorProps {
  fieldDef: FilterFieldDef;
  filter?: ActiveFilter;
  onApply: (val: string) => void;
  onClose: () => void;
}

export function TextFilterEditor({
  fieldDef,
  filter,
  onApply,
  onClose,
}: TextFilterEditorProps) {
  const { t } = useI18n();
  const [val, setVal] = useState(filter?.value || '');
  const [options, setOptions] = useState<FilterOption[]>([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Fetch dynamic or static options if available
  useEffect(() => {
    if (!fieldDef.options) return;
    if (typeof fieldDef.options === 'function') {
      setLoading(true);
      fieldDef
        .options()
        .then((res) => setOptions(res))
        .catch(() => setOptions([]))
        .finally(() => setLoading(false));
    } else {
      setOptions(fieldDef.options);
    }
  }, [fieldDef]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (val.trim()) {
      onApply(val.trim());
      onClose();
    }
  };

  const filteredOptions = val
    ? options.filter(
        (opt) =>
          t(opt.label as any).toLowerCase().includes(val.toLowerCase()) ||
          opt.value.toLowerCase().includes(val.toLowerCase())
      )
    : options;

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="relative">
        <Search className="w-4 h-4 sm:w-3.5 sm:h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
        <input
          ref={inputRef}
          type="text"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder={fieldDef.placeholder ? t(fieldDef.placeholder as any) : `${t('filter.searchOptions')}`}
          className="w-full pl-9 pr-8 py-2.5 sm:py-1.5 bg-bg-surface border border-border-default rounded-lg text-sm sm:text-xs text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent transition-colors"
        />
        {val && (
          <button
            type="button"
            onClick={() => setVal('')}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-text-muted hover:text-text-primary"
          >
            <X size={13} />
          </button>
        )}
      </div>

      {loading && (
        <div className="text-xs text-text-muted text-center py-4">
          {t('filter.loadingOptions')}
        </div>
      )}

      {!loading && filteredOptions.length > 0 && (
        <div className="max-h-[45dvh] sm:max-h-40 overflow-y-auto space-y-1 pr-1">
          {filteredOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                onApply(opt.value);
                onClose();
              }}
              className="w-full flex items-center justify-between p-2.5 sm:p-1.5 rounded-lg text-sm sm:text-xs text-left text-text-secondary hover:text-text-primary hover:bg-bg-hover transition-colors cursor-pointer"
            >
              <div className="flex items-center gap-2.5 truncate">
                {opt.icon && <opt.icon className="w-4 h-4 sm:w-3.5 sm:h-3.5 text-text-muted" />}
                <span className="truncate">{t(opt.label as any)}</span>
              </div>
              {val === opt.value && <Check className="w-4 h-4 sm:w-3.5 sm:h-3.5 text-accent shrink-0" />}
            </button>
          ))}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-3 border-t border-border-subtle">
        <button
          type="button"
          onClick={onClose}
          className="px-3 py-2 sm:py-1.5 rounded-lg text-xs font-medium text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors cursor-pointer"
        >
          {t('filter.cancel')}
        </button>
        <button
          type="submit"
          className="px-4 py-2 sm:py-1.5 rounded-lg text-xs font-medium bg-accent text-white hover:bg-accent-hover transition-colors cursor-pointer shadow-sm"
        >
          {t('filter.apply')}
        </button>
      </div>
    </form>
  );
}
